import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TableSortLabel, CircularProgress, Alert, Chip, Tooltip,
  Snackbar, TablePagination, TextField, InputAdornment, Select, MenuItem,
  FormControl, InputLabel, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, Divider, LinearProgress, Checkbox, Grid,
  Fab,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DescriptionIcon from '@mui/icons-material/Description';
import ArticleIcon from '@mui/icons-material/Article';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import {
  getRicefwItems, generateRicefwItems, downloadRicefwExcel,
  uploadRicefwPreview, uploadRicefwApply, updateRicefwItem, deleteRicefwItems,
  generateFsDocuments,
  generateTsDocuments,
  copyRicefwItems,
  createRicefwItem,
  getRicefwSpecSummary,
  getRicefwSpecDocument,
  downloadRicefwSpecDocument,
  previewRicefwSpecDocumentUpload,
  uploadRicefwSpecDocument,
} from '../../utils/ricefwService';
import { Joyride, STATUS, ACTIONS } from 'react-joyride';
import SafetyConfirmDialog from '../../components/SafetyConfirmDialog';

const TYPE_COLORS = {
  R: { bg: '#E3F2FD', color: '#1565C0', label: 'Report' },
  I: { bg: '#E8F5E9', color: '#2E7D32', label: 'Interface' },
  C: { bg: '#FFF3E0', color: '#E65100', label: 'Conversion' },
  E: { bg: '#F3E5F5', color: '#6A1B9A', label: 'Enhancement' },
  F: { bg: '#FCE4EC', color: '#880E4F', label: 'Forms' },
  W: { bg: '#E0F7FA', color: '#006064', label: 'Workflow' },
};

// All 30 columns
const VISIBLE_COLUMNS = [
  { field: 'ricefw_type',                     label: 'Type',                                 width: 90 },
  { field: 'ricefw_id',                       label: 'RICEFW ID',                            width: 110 },
  { field: 'bpd',                             label: 'BPD',                                  width: 90 },
  { field: 'workstream',                      label: 'Workstream',                           width: 180 },
  { field: 'ricefw_description',              label: 'RICEFW Description',                   width: 260 },
  { field: 'requirement',                     label: 'Requirement',                          width: 260 },
  { field: 'fs_owner',                        label: 'FS Owner',                             width: 160 },
  { field: 'source_system',                   label: 'Source System (Interfaces)',           width: 160 },
  { field: 'target_system',                   label: 'Target System (Interfaces)',           width: 160 },
  { field: 'ricefw_complexity',               label: 'RICEFW Complexity',                   width: 130 },
  { field: 'priority',                        label: 'Priority',                             width: 100 },
  { field: 'sprint',                          label: 'Sprint',                               width: 90 },
  { field: 'mock_sit_number',                 label: 'Mock# and SIT#',                      width: 120 },
  { field: 'posting_date',                    label: 'Posting Date',                        width: 120 },
  { field: 'technical_resource_time',         label: 'Technical Resource Time',             width: 170 },
  { field: 'fs_name',                         label: 'FS Name',                              width: 180 },
  { field: 'fs_start_week',                   label: 'FS Start Week',                       width: 120 },
  { field: 'fs_end_week',                     label: 'FS End Week',                         width: 120 },
  { field: 'fs_due_date',                     label: 'FS Due Date',                         width: 120 },
  { field: 'ts_owner',                        label: 'TS Owner',                             width: 160 },
  { field: 'ts_due_date',                     label: 'TS Due Date',                         width: 120 },
  { field: 'developer_fb',                    label: 'Developer F/B',                       width: 130 },
  { field: 'development_start_date',          label: 'Development Start Date',              width: 170 },
  { field: 'development_end_date',            label: 'Development End Date',                width: 170 },
  { field: 'comments_status',                 label: 'Comments and Status of dev progress', width: 280 },
  { field: 'overall_status',                  label: 'Overall Status',                      width: 140 },
  { field: 'sort_order',                      label: 'Sort Order',                          width: 100 },
  { field: 'link_to_template',                label: 'Link to Template',                    width: 200 },
  { field: 'reporting_priority',              label: 'Reporting - Priority',                width: 150 },
  { field: 'reporting_business_report_owner', label: 'Reporting - Business Report Owner',  width: 220 },
];

// Fields the user can edit in the modal (auto-generated ones are read-only)
const READONLY_FIELDS = new Set(['ricefw_type', 'ricefw_id']);

const EMPTY_FILTERS = { type: '' };

const formatDateTime = (value) => {
  if (!value) return 'Not available';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
};

const baselineFromDocument = (doc) => {
  if (!doc) return null;
  return {
    version_number: doc.version_number,
    version_label: doc.version_label,
    file_name: doc.file_name,
    downloaded_at: doc.last_downloaded_at || null,
    downloaded_by: doc.last_downloaded_by || null,
  };
};

function descendingComparator(a, b, field) {
  const av = (a[field] ?? '').toString().toLowerCase();
  const bv = (b[field] ?? '').toString().toLowerCase();
  if (bv < av) return -1;
  if (bv > av) return 1;
  return 0;
}
function getComparator(order, field) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, field)
    : (a, b) => -descendingComparator(a, b, field);
}

// ── Edit Modal ─────────────────────────────────────────────────────────────
const EditModal = ({ open, item, onClose, onSaved, initialReadOnly = false }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [viewMode, setViewMode] = useState(initialReadOnly);

  useEffect(() => {
    if (item) { setForm({ ...item }); setViewMode(initialReadOnly); setSaveError(null); }
  }, [item, initialReadOnly]);

  if (!item) return null;

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const payload = {};
      VISIBLE_COLUMNS.forEach(({ field }) => {
        if (!READONLY_FIELDS.has(field)) payload[field] = form[field] ?? '';
      });
      const result = await updateRicefwItem(item.id, payload);
      if (result.success) {
        onSaved(result.item);
      } else {
        setSaveError(result.error || 'Save failed');
      }
    } catch (e) {
      setSaveError('Save failed: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const meta = TYPE_COLORS[item.ricefw_type] || { bg: '#F5F5F5', color: '#424242', label: item.ricefw_type };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ fontWeight: 700, borderBottom: '1px solid rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', gap: 1 }}>
        <Chip label={`${item.ricefw_type} – ${meta.label}`} size="small"
          sx={{ bgcolor: meta.bg, color: meta.color, fontWeight: 600 }} />
        <Typography fontFamily="monospace" fontWeight={700}>{item.ricefw_id}</Typography>
        <Typography variant="body2" color="text.secondary" noWrap sx={{ ml: 1, maxWidth: 260 }}>{item.ricefw_description}</Typography>
        <Chip label={viewMode ? 'View' : 'Edit'} size="small" variant="outlined"
          sx={{ ml: 'auto', color: viewMode ? '#90A4AE' : '#9C27B0', borderColor: viewMode ? '#90A4AE' : '#9C27B0', fontWeight: 600, fontSize: '0.7rem' }} />
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Grid container spacing={2}>
          {VISIBLE_COLUMNS.map(({ field, label }) => {
            const fieldReadOnly = viewMode || READONLY_FIELDS.has(field);
            const isIdentity = READONLY_FIELDS.has(field);
            const isMultiline = ['ricefw_description', 'requirement', 'comments_status', 'link_to_template'].includes(field);
            return (
              <Grid item xs={12} sm={field === 'comments_status' || field === 'ricefw_description' || field === 'requirement' ? 12 : 6} key={field}>
                <TextField
                  fullWidth
                  size="small"
                  label={label}
                  value={form[field] ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, [field]: e.target.value }))}
                  disabled={fieldReadOnly}
                  multiline={isMultiline}
                  minRows={isMultiline ? 2 : 1}
                  InputProps={{ readOnly: fieldReadOnly }}
                  sx={{ '& .MuiInputBase-root': { bgcolor: 'transparent' } }}
                  helperText={isIdentity ? 'Cannot be changed' : undefined}
                />
              </Grid>
            );
          })}
        </Grid>
        {saveError && <Alert severity="error" sx={{ mt: 2 }}>{saveError}</Alert>}
        {saving && <LinearProgress sx={{ mt: 2 }} />}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
        <Button onClick={onClose} disabled={saving}>{viewMode ? 'Close' : 'Cancel'}</Button>
        {viewMode ? (
          <Button variant="contained" startIcon={<EditIcon />} onClick={() => setViewMode(false)}
            sx={{ bgcolor: '#9C27B0', '&:hover': { bgcolor: '#7B1FA2' } }}>
            Edit
          </Button>
        ) : (
          <Button variant="contained" onClick={handleSave} disabled={saving}
            sx={{ bgcolor: '#9C27B0', '&:hover': { bgcolor: '#7B1FA2' } }}>
            {saving ? 'Saving…' : 'Save Changes'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// ── Preview Modal ──────────────────────────────────────────────────────────
const PreviewModal = ({ open, previewData, pendingFile, projectId, onClose, onApplied }) => {
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState(null);

  if (!previewData) return null;
  const { summary, changes, not_found_ids } = previewData;

  const handleApply = async () => {
    setApplying(true);
    setApplyError(null);
    try {
      const result = await uploadRicefwApply(projectId, pendingFile);
      if (result.success) onApplied(result.message || `Updated ${result.updated} items.`);
      else setApplyError(result.error || 'Apply failed');
    } catch (e) {
      setApplyError('Apply failed: ' + e.message);
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ fontWeight: 700, borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
        Upload Preview — Review Changes
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          {[
            { label: 'Rows in file', value: summary.total_in_file, color: '#1976D2' },
            { label: 'Changed',      value: summary.changed,       color: summary.changed > 0 ? '#E65100' : '#2E7D32' },
            { label: 'Unchanged',    value: summary.unchanged,     color: '#555' },
            { label: 'Not found',    value: summary.not_found,     color: summary.not_found > 0 ? '#C62828' : '#555' },
          ].map(({ label, value, color }) => (
            <Paper key={label} variant="outlined" sx={{ px: 2.5, py: 1.5, textAlign: 'center', minWidth: 110 }}>
              <Typography variant="h5" fontWeight={700} sx={{ color }}>{value}</Typography>
              <Typography variant="caption" color="text.secondary">{label}</Typography>
            </Paper>
          ))}
        </Box>
        {not_found_ids.length > 0 && (
          <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
            <strong>{not_found_ids.length} RICEFW ID(s) not found</strong> and will be skipped:{' '}
            {not_found_ids.slice(0, 10).join(', ')}{not_found_ids.length > 10 ? ` …+${not_found_ids.length - 10} more` : ''}
          </Alert>
        )}
        {changes.length === 0 ? (
          <Alert severity="info" icon={<CheckCircleOutlineIcon />}>No changes detected.</Alert>
        ) : (
          <>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>{changes.length} row{changes.length !== 1 ? 's' : ''} with changes:</Typography>
            <Box sx={{ maxHeight: 400, overflowY: 'auto', border: '1px solid rgba(0,0,0,0.1)', borderRadius: 1 }}>
              {changes.map((row, i) => {
                const meta = TYPE_COLORS[row.ricefw_type] || { bg: '#F5F5F5', color: '#424242', label: row.ricefw_type };
                return (
                  <Box key={row.ricefw_id}>
                    {i > 0 && <Divider />}
                    <Box sx={{ p: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Chip label={`${row.ricefw_type} – ${meta.label}`} size="small" sx={{ bgcolor: meta.bg, color: meta.color, fontWeight: 600, fontSize: '0.7rem' }} />
                        <Typography variant="body2" fontFamily="monospace" fontWeight={700}>{row.ricefw_id}</Typography>
                        {row.ricefw_description && <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 300 }}>{row.ricefw_description}</Typography>}
                        <Chip label={`${row.field_changes.length} field${row.field_changes.length !== 1 ? 's' : ''}`} size="small" color="warning" variant="outlined" sx={{ ml: 'auto' }} />
                      </Box>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#37474F' }}>
                            <TableCell sx={{ fontWeight: 600, py: 0.5, width: '30%', color: '#fff' }}>Field</TableCell>
                            <TableCell sx={{ fontWeight: 600, py: 0.5, color: '#EF9A9A', width: '35%' }}>Current value</TableCell>
                            <TableCell sx={{ fontWeight: 600, py: 0.5, color: '#A5D6A7', width: '35%' }}>New value</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {row.field_changes.map((fc) => (
                            <TableRow key={fc.field}>
                              <TableCell sx={{ py: 0.4, fontSize: '0.78rem' }}>{fc.label}</TableCell>
                              <TableCell sx={{ py: 0.4, fontSize: '0.78rem', color: '#C62828', fontStyle: fc.old ? 'normal' : 'italic' }}>{fc.old || '(empty)'}</TableCell>
                              <TableCell sx={{ py: 0.4, fontSize: '0.78rem', color: '#2E7D32', fontStyle: fc.new ? 'normal' : 'italic' }}>{fc.new || '(empty)'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </>
        )}
        {applyError && <Alert severity="error" sx={{ mt: 2 }}>{applyError}</Alert>}
        {applying && <LinearProgress sx={{ mt: 2 }} />}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
        <Button onClick={onClose} disabled={applying}>Cancel</Button>
        <Button variant="contained" onClick={handleApply} disabled={applying || changes.length === 0}
          sx={{ bgcolor: '#9C27B0', '&:hover': { bgcolor: '#7B1FA2' } }}>
          {applying ? 'Applying…' : `Apply ${changes.length} Change${changes.length !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};


// ── FS/TS Document Dialog ───────────────────────────────────────────────────
const SpecDocumentDialog = ({ open, projectId, item, specType, onClose, onChanged, showSnackbar }) => {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloadingVersion, setDownloadingVersion] = useState(null);
  const [previewingUpload, setPreviewingUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [changeNote, setChangeNote] = useState('');
  const [downloadedBase, setDownloadedBase] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [dialogError, setDialogError] = useState(null);

  const isFunctional = specType === 'functional';
  const title = isFunctional ? 'Functional Specification' : 'Technical Specification';
  const meta = TYPE_COLORS[item?.ricefw_type] || { bg: '#F5F5F5', color: '#424242', label: item?.ricefw_type };
  const latest = state?.latest;
  const versions = state?.versions || [];
  const trimmedChangeNote = changeNote.trim();
  const busy = uploading || previewingUpload || Boolean(downloadingVersion);
  const uploadTargetLabel = uploadPreview?.next_version_label || 'document version';

  const loadState = useCallback(async () => {
    if (!open || !item || !specType) return null;
    setLoading(true);
    setDialogError(null);
    try {
      const result = await getRicefwSpecDocument(projectId, item.id, specType);
      if (result.success) {
        setState(result);
        setDownloadedBase(baselineFromDocument(result.upload_baseline));
        return result;
      }
      setDialogError(result.error || 'Failed to load document details');
      return null;
    } catch (e) {
      setDialogError('Failed to load document details');
      return null;
    } finally {
      setLoading(false);
    }
  }, [open, projectId, item, specType]);

  useEffect(() => {
    if (open) {
      setSelectedFile(null);
      setChangeNote('');
      setDownloadedBase(null);
      setUploadPreview(null);
      loadState();
    }
  }, [open, loadState]);

  if (!item) return null;

  const findVersion = (result, version) => (result?.versions || []).find((doc) => doc.version_number === version);

  const handleDownload = async (version = null) => {
    const label = version === null ? 'latest' : `v${version}`;
    setDownloadingVersion(label);
    setDialogError(null);
    try {
      const result = await downloadRicefwSpecDocument(projectId, item.id, specType, version);
      if (result.ok) {
        const refreshed = await loadState();
        const baseDoc = version === null ? refreshed?.latest : findVersion(refreshed, version);
        if (baseDoc) {
          setDownloadedBase({
            version_number: baseDoc.version_number,
            version_label: baseDoc.version_label,
            file_name: baseDoc.file_name,
            downloaded_at: baseDoc.last_downloaded_at || new Date().toISOString(),
            downloaded_by: baseDoc.last_downloaded_by || null,
          });
        }
        setUploadPreview(null);
        showSnackbar(version === null ? `${title} downloaded.` : `${title} v${version} downloaded.`);
        onChanged?.();
      } else {
        setDialogError(result.error || 'Download failed');
      }
    } catch (e) {
      setDialogError('Download failed: ' + e.message);
    } finally {
      setDownloadingVersion(null);
    }
  };

  const validateUploadInputs = () => {
    if (!selectedFile) return 'Please choose a .docx file to upload.';
    if (!selectedFile.name.toLowerCase().endsWith('.docx')) return 'Only .docx files are supported.';
    return null;
  };

  const handlePreviewUpload = async () => {
    const validationError = validateUploadInputs();
    if (validationError) {
      setDialogError(validationError);
      return;
    }
    setPreviewingUpload(true);
    setDialogError(null);
    setUploadPreview(null);
    try {
      const result = await previewRicefwSpecDocumentUpload(
        projectId,
        item.id,
        specType,
        selectedFile,
        trimmedChangeNote,
        downloadedBase?.version_number ?? null
      );
      if (result.success) {
        setChangeNote(result.change_note || '');
        if (result.base_document) {
          setDownloadedBase(baselineFromDocument(result.base_document));
        }
        setUploadPreview(result);
      } else {
        setDialogError(result.error || 'Upload review failed');
      }
    } catch (e) {
      setDialogError('Upload review failed: ' + e.message);
    } finally {
      setPreviewingUpload(false);
    }
  };

  const handleConfirmUpload = async () => {
    const validationError = validateUploadInputs();
    if (validationError) {
      setDialogError(validationError);
      setUploadPreview(null);
      return;
    }
    setUploading(true);
    setDialogError(null);
    try {
      const result = await uploadRicefwSpecDocument(
        projectId,
        item.id,
        specType,
        selectedFile,
        trimmedChangeNote,
        downloadedBase?.version_number ?? null
      );
      if (result.success) {
        showSnackbar(result.message || `${title} uploaded.`);
        setSelectedFile(null);
        setChangeNote('');
        setDownloadedBase(null);
        setUploadPreview(null);
        await loadState();
        onChanged?.();
      } else {
        setDialogError(result.error || 'Upload failed');
      }
    } catch (e) {
      setDialogError('Upload failed: ' + e.message);
    } finally {
      setUploading(false);
    }
  };

  const diff = uploadPreview?.diff;
  const diffSummary = diff?.summary;
  const diffChanges = diff?.changes || [];

  return (
    <>
      <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle sx={{ fontWeight: 700, borderBottom: '1px solid rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', gap: 1 }}>
          {isFunctional ? <DescriptionIcon sx={{ color: '#66BB6A' }} /> : <ArticleIcon sx={{ color: '#80CBC4' }} />}
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" fontWeight={700}>{title} - {item.ricefw_id}</Typography>
            <Typography variant="body2" color="text.secondary" noWrap>{item.ricefw_description || 'No description available'}</Typography>
          </Box>
          <Chip label={`${item.ricefw_type} - ${meta.label}`} size="small"
            sx={{ ml: 'auto', bgcolor: meta.bg, color: meta.color, fontWeight: 600 }} />
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : (
            <>
              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                {[
                  ['RICEFW ID', item.ricefw_id],
                  ['Workstream', item.workstream],
                  [isFunctional ? 'FS Owner' : 'TS Owner', isFunctional ? item.fs_owner : (item.ts_owner || item.fs_owner)],
                  ['Requirement', item.requirement],
                ].map(([label, value]) => (
                  <Grid item xs={12} sm={label === 'Requirement' ? 12 : 4} key={label}>
                    <Paper variant="outlined" sx={{ p: 1.25, height: '100%' }}>
                      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{value || 'Not available'}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: latest ? 'rgba(46,125,50,0.35)' : 'rgba(245,130,31,0.45)' }}>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <Box sx={{ flex: '1 1 280px' }}>
                    <Typography variant="subtitle1" fontWeight={700}>Latest Document</Typography>
                    {latest ? (
                      <>
                        <Typography variant="body2">
                          <strong>{latest.version_label}</strong> - {latest.file_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {latest.source === 'uploaded' ? 'Uploaded' : 'Created from template'} by {latest.created_by_name || 'Unknown'} on {formatDateTime(latest.created_at)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Last downloaded: {latest.last_downloaded_at ? `${latest.last_downloaded_by || 'Unknown'} on ${formatDateTime(latest.last_downloaded_at)}` : 'Not yet downloaded'}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No managed version exists yet. Downloading will create and store the initial template-filled document as v0. Uploading first will also preserve that v0 before storing your upload as v1.
                      </Typography>
                    )}
                  </Box>
                  <Button variant="contained" startIcon={downloadingVersion === 'latest' ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
                    onClick={() => handleDownload(null)}
                    disabled={busy}
                    sx={{ bgcolor: '#1565C0', '&:hover': { bgcolor: '#0D47A1' } }}>
                    {downloadingVersion === 'latest' ? 'Downloading...' : latest ? 'Download Latest' : 'Download Document'}
                  </Button>
                </Box>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>Upload New Version</Typography>
                {downloadedBase ? (
                  <Alert severity="info" sx={{ mb: 1.5 }}>
                    Changes will be reviewed against the selected baseline: <strong>{downloadedBase.version_label}</strong> ({downloadedBase.file_name})
                    {downloadedBase.downloaded_by || downloadedBase.downloaded_at
                      ? `, downloaded${downloadedBase.downloaded_by ? ` by ${downloadedBase.downloaded_by}` : ''}${downloadedBase.downloaded_at ? ` on ${formatDateTime(downloadedBase.downloaded_at)}` : ''}.`
                      : '.'}
                  </Alert>
                ) : latest ? (
                  <Alert severity="warning" sx={{ mb: 1.5 }}>
                    No comparison baseline is selected for this upload. Download a managed version first to compare changes, or continue without comparison.
                  </Alert>
                ) : (
                  <Alert severity="warning" sx={{ mb: 1.5 }}>
                    No managed version exists yet. The first upload will preserve the template-filled document as v0 before saving your upload as v1.
                  </Alert>
                )}
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mb: 1.5 }}>
                  <Button variant="outlined" component="label" startIcon={<UploadFileIcon />} disabled={busy}>
                    {selectedFile ? 'Change File' : 'Choose DOCX'}
                    <input hidden type="file" accept=".docx" onChange={(e) => { setSelectedFile(e.target.files?.[0] || null); setUploadPreview(null); }} />
                  </Button>
                  <Typography variant="body2" color={selectedFile ? 'text.primary' : 'text.secondary'}>
                    {selectedFile ? selectedFile.name : 'No file selected'}
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  label="Change note"
                  placeholder="Briefly describe what changed in this version"
                  value={changeNote}
                  onChange={(e) => { setChangeNote(e.target.value); setUploadPreview(null); }}
                  multiline
                  minRows={2}
                  helperText="Optional. If the document history has a Reason for Change, it will be used here."
                  sx={{ mb: 1.5 }}
                />
                <Button variant="contained" startIcon={previewingUpload ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />}
                  onClick={handlePreviewUpload}
                  disabled={!selectedFile || busy}
                  sx={{ bgcolor: '#2E7D32', '&:hover': { bgcolor: '#1B5E20' } }}>
                  {previewingUpload ? 'Reviewing...' : uploadPreview ? `Review Upload as ${uploadTargetLabel}` : 'Review Upload'}
                </Button>
              </Paper>

              <Typography variant="subtitle1" fontWeight={700} gutterBottom>Version History</Typography>
              {versions.length === 0 ? (
                <Alert severity="info">No versions yet. Download the document to create v0, or upload a revised document. The first upload will preserve the template-filled document as v0 before saving the upload as v1.</Alert>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#37474F' }}>
                      <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Version</TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Source</TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 700 }}>File</TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Created</TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Note</TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {versions.map((doc) => (
                      <TableRow key={doc.id}>
                        <TableCell><Chip label={doc.version_label} size="small" /></TableCell>
                        <TableCell>{doc.source === 'uploaded' ? 'Uploaded' : 'Template'}</TableCell>
                        <TableCell>{doc.file_name}</TableCell>
                        <TableCell>{doc.created_by_name || 'Unknown'}<br /><Typography variant="caption" color="text.secondary">{formatDateTime(doc.created_at)}</Typography></TableCell>
                        <TableCell>{doc.change_note || '-'}</TableCell>
                        <TableCell align="right">
                          <Button size="small" startIcon={downloadingVersion === doc.version_label ? <CircularProgress size={14} /> : <DownloadIcon />}
                            onClick={() => handleDownload(doc.version_number)}
                            disabled={busy}>
                            Download
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          )}
          {dialogError && <Alert severity="error" sx={{ mt: 2 }}>{dialogError}</Alert>}
          {busy && <LinearProgress sx={{ mt: 2 }} />}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
          <Button onClick={onClose} disabled={busy}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(uploadPreview)} onClose={uploading ? undefined : () => setUploadPreview(null)} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle sx={{ fontWeight: 700, borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
          Review Changes Before Upload
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {uploadPreview && (
            <>
              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                {[
                  ['Base document', uploadPreview.base_document ? `${uploadPreview.base_document.version_label} - ${uploadPreview.base_document.file_name}` : 'No comparison baseline selected'],
                  ['New upload', uploadPreview.uploaded_file_name],
                  ['Upload version', uploadPreview.next_version_label],
                  ['Change note', uploadPreview.change_note],
                ].map(([label, value]) => (
                  <Grid item xs={12} sm={label === 'Change note' ? 12 : 4} key={label}>
                    <Paper variant="outlined" sx={{ p: 1.25, height: '100%' }}>
                      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{value || 'Not available'}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              {uploadPreview.message && <Alert severity="warning" sx={{ mb: 2 }}>{uploadPreview.message}</Alert>}
              {diff?.available ? (
                <>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    <Chip label={`${diffSummary?.change_count || 0} text/table change${diffSummary?.change_count === 1 ? '' : 's'}`} color={(diffSummary?.change_count || 0) > 0 ? 'warning' : 'success'} />
                    <Chip label={`${diffSummary?.paragraphs_before || 0} -> ${diffSummary?.paragraphs_after || 0} paragraphs`} variant="outlined" />
                    <Chip label={`${diffSummary?.table_cells_before || 0} -> ${diffSummary?.table_cells_after || 0} table cells`} variant="outlined" />
                  </Box>
                  {diffSummary?.truncated && <Alert severity="info" sx={{ mb: 2 }}>Only the first {diffChanges.length} changes are shown.</Alert>}
                  {diffChanges.length === 0 ? (
                    <Alert severity="info">No text or table-cell changes were detected. Formatting-only changes may not be shown.</Alert>
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#37474F' }}>
                          <TableCell sx={{ color: '#fff', fontWeight: 700, width: '16%' }}>Type</TableCell>
                          <TableCell sx={{ color: '#fff', fontWeight: 700, width: '42%' }}>Downloaded version</TableCell>
                          <TableCell sx={{ color: '#fff', fontWeight: 700, width: '42%' }}>Uploaded document</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {diffChanges.map((change, index) => (
                          <TableRow key={`${change.type}-${index}`}>
                            <TableCell><Chip label={change.type} size="small" /></TableCell>
                            <TableCell sx={{ whiteSpace: 'pre-wrap', verticalAlign: 'top' }}>{change.before || '-'}</TableCell>
                            <TableCell sx={{ whiteSpace: 'pre-wrap', verticalAlign: 'top' }}>{change.after || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </>
              ) : !uploadPreview.message ? (
                <Alert severity="warning">The exact document differences could not be assessed. Please make sure you are uploading the correct document before continuing.</Alert>
              ) : null}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
          <Button onClick={() => setUploadPreview(null)} disabled={uploading}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirmUpload} disabled={uploading}
            startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />}
            sx={{ bgcolor: '#2E7D32', '&:hover': { bgcolor: '#1B5E20' } }}>
            {uploading ? 'Uploading...' : `Confirm Upload as ${uploadTargetLabel}`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
// ── Create Row Modal ───────────────────────────────────────────────────────
const PREFIX_MAP = { R: 'RPT', I: 'INT', C: 'CNV', E: 'ENH', F: 'FRM', W: 'WFL' };

const CreateRowModal = ({ open, projectId, onClose, onCreated }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    if (open) { setForm({}); setSaveError(null); }
  }, [open]);

  const selectedType = form.ricefw_type || '';

  const handleSave = async () => {
    if (!selectedType) { setSaveError('Please select a RICEFW Type.'); return; }
    setSaving(true);
    setSaveError(null);
    try {
      const result = await createRicefwItem(projectId, form);
      if (result.success) {
        onCreated(result.item);
      } else {
        setSaveError(result.error || 'Create failed');
      }
    } catch (e) {
      setSaveError('Create failed: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ fontWeight: 700, borderBottom: '1px solid rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', gap: 1 }}>
        <AddIcon sx={{ color: '#9C27B0' }} />
        Add New RICEFW Item
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Grid container spacing={2}>
          {/* Type selector */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small" required>
              <InputLabel>RICEFW Type *</InputLabel>
              <Select
                label="RICEFW Type *"
                value={selectedType}
                onChange={(e) => setForm((f) => ({ ...f, ricefw_type: e.target.value }))}
              >
                {Object.entries(TYPE_COLORS).map(([k, v]) => (
                  <MenuItem key={k} value={k}>{k} – {v.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {/* RICEFW ID preview */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth size="small" label="RICEFW ID"
              value={selectedType ? `Auto-generated (e.g. ${PREFIX_MAP[selectedType]}XXXX)` : 'Select type first'}
              disabled
              sx={{ '& .MuiInputBase-root': { bgcolor: 'transparent' } }}
              helperText="Auto-generated by the system"
            />
          </Grid>
          {/* All editable fields */}
          {VISIBLE_COLUMNS.filter(({ field }) => field !== 'ricefw_type' && field !== 'ricefw_id').map(({ field, label }) => {
            const isMultiline = ['ricefw_description', 'requirement', 'comments_status', 'link_to_template'].includes(field);
            return (
              <Grid item xs={12} sm={['comments_status', 'ricefw_description', 'requirement'].includes(field) ? 12 : 6} key={field}>
                <TextField
                  fullWidth size="small" label={label}
                  value={form[field] ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, [field]: e.target.value }))}
                  multiline={isMultiline}
                  minRows={isMultiline ? 2 : 1}
                  sx={{ '& .MuiInputBase-root': { bgcolor: 'transparent' } }}
                />
              </Grid>
            );
          })}
        </Grid>
        {saveError && <Alert severity="error" sx={{ mt: 2 }}>{saveError}</Alert>}
        {saving && <LinearProgress sx={{ mt: 2 }} />}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || !selectedType}
          sx={{ bgcolor: '#9C27B0', '&:hover': { bgcolor: '#7B1FA2' } }}>
          {saving ? 'Creating…' : 'Create Item'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};


// ── Main Page ──────────────────────────────────────────────────────────────
const RICEFWListPage = () => {
  const { projectId } = useParams();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [page, setPage] = useState(0);
  const rowsPerPage = 25;

  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [sortField, setSortField] = useState('ricefw_id');
  const [sortOrder, setSortOrder] = useState('asc');

  // Selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [generatingFsIds, setGeneratingFsIds] = useState(new Set());
  const [generatingTsIds, setGeneratingTsIds] = useState(new Set());
  const [confirmGenerateOpen, setConfirmGenerateOpen] = useState(false);
  const [specSummary, setSpecSummary] = useState({});
  const [specDialog, setSpecDialog] = useState(null);
  const [specConfirm, setSpecConfirm] = useState(null);

  // Edit modal
  const [editItem, setEditItem] = useState(null);
  const [editItemReadOnly, setEditItemReadOnly] = useState(false);

  // Copy & Create
  const [copying, setCopying] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const [tourRun, setTourRun] = useState(false);

  const tourSteps = [
    {
      target: '[data-tour="generate-btn"]',
      title: items.length > 0 ? '✨ Regenerate RICEFW List' : '✨ Generate RICEFW List',
      content: items.length > 0
        ? "Click here to regenerate RICEFW items from your Gap Analysis backlog. You'll be asked to confirm before the existing list is overwritten."
        : 'Click here to generate RICEFW items from your Gap Analysis backlog. Each backlog item becomes a typed entry: Report, Interface, Conversion, Enhancement, Forms, or Workflow.',
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: '[data-tour="download-btn"]',
      title: '⬇️ Download Excel',
      content: 'Export the full RICEFW list as an .xlsx file. Open it in Excel to fill in FS Owner, Complexity, Priority, Sprint, Dates, and Developer fields. Keep column structure intact before re-uploading.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="upload-btn"]',
      title: '⬆️ Upload Excel',
      content: "Import your edited Excel file back. You'll see a preview of every change (current vs new value) before anything is saved. Only existing RICEFW IDs are updated — unknown IDs are skipped and flagged.",
      placement: 'bottom',
    },
    {
      target: '[data-tour="type-filter"]',
      title: '🔍 Filter by Type',
      content: 'Filter the list by RICEFW category: R = Report, I = Interface, C = Conversion, E = Enhancement, F = Forms, W = Workflow. Combine with the search box to narrow down quickly.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="search-box"]',
      title: '🔎 Search All Fields',
      content: 'Search across all 30 columns at once — RICEFW ID, description, owner, workstream, comments, and more. Click any column header to sort.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="table-header-checkbox"]',
      title: '☑️ Select Items',
      content: 'Check the header box to select all items on the current page. Check individual rows to select specific items. Once selected, bulk action buttons appear: Generate Functional Spec, Generate Technical Spec, or Delete.',
      placement: 'right',
    },
    {
      target: '[data-tour="actions-header"]',
      title: '⚡ Row Actions',
      content: 'Each row has action icons: ✏️ Edit (blue) — open the edit dialog to update fields. 🗑️ Delete (red) — remove this item. 📄 Functional Spec (green) and 📋 Technical Spec (teal) — open document options for download, upload, and history.',
      placement: 'right',
    },
  ];

  // Upload
  const fileInputRef = useRef(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);

  const showSnackbar = (msg, sev = 'success') => setSnackbar({ open: true, message: msg, severity: sev });

  const fetchSpecSummary = useCallback(async () => {
    try {
      const result = await getRicefwSpecSummary(projectId);
      if (result.success) setSpecSummary(result.summary || {});
    } catch (e) {
      // Keep the table usable even if document status metadata is unavailable.
    }
  }, [projectId]);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getRicefwItems(projectId);
      if (data.success) {
        setItems(data.items);
        setPage(0);
        setSelectedIds(new Set());
        await fetchSpecSummary();
      }
      else setError(data.error || 'Failed to load RICEFW items');
    } catch (e) { setError('Failed to load RICEFW items'); }
    finally { setLoading(false); }
  }, [projectId, fetchSpecSummary]);

  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => { setPage(0); }, [search, filters, sortField, sortOrder]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const data = await generateRicefwItems(projectId);
      if (data.success) { showSnackbar(data.message); await fetchItems(); }
      else showSnackbar(data.error || 'Generation failed', 'error');
    } catch (e) { showSnackbar('Generation failed', 'error'); }
    finally { setGenerating(false); }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const result = await downloadRicefwExcel(projectId);
      if (result.ok) showSnackbar('RICEFW list downloaded successfully');
      else showSnackbar(result.error || 'Download failed', 'error');
    } catch (e) { showSnackbar('Download failed', 'error'); }
    finally { setDownloading(false); }
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setPreviewing(true);
    try {
      const result = await uploadRicefwPreview(projectId, file);
      if (result.success) { setPendingFile(file); setPreviewData(result); setPreviewOpen(true); }
      else showSnackbar(result.error || 'Preview failed', 'error');
    } catch (err) { showSnackbar('Failed to parse file: ' + err.message, 'error'); }
    finally { setPreviewing(false); }
  };

  const handlePreviewClose = () => { setPreviewOpen(false); setPreviewData(null); setPendingFile(null); };
  const handleApplied = (msg) => { setPreviewOpen(false); setPreviewData(null); setPendingFile(null); showSnackbar(msg); fetchItems(); };

  // Edit
  const handleEditSaved = (updatedItem) => {
    setItems((prev) => prev.map((i) => (i.id === updatedItem.id ? updatedItem : i)));
    setEditItem(null);
    showSnackbar(`${updatedItem.ricefw_id} updated successfully`);
  };

  // Delete
  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      const ids = [...selectedIds];
      const result = await deleteRicefwItems(projectId, ids);
      if (result.success) {
        showSnackbar(`Deleted ${result.deleted} item${result.deleted !== 1 ? 's' : ''}`);
        setSelectedIds(new Set());
        setConfirmDeleteOpen(false);
        await fetchItems();
      } else showSnackbar(result.error || 'Delete failed', 'error');
    } catch (e) { showSnackbar('Delete failed', 'error'); }
    finally { setDeleting(false); }
  };

  const handleDeleteSingle = (item) => { setSelectedIds(new Set([item.id])); setConfirmDeleteOpen(true); };

  const handleCopy = async () => {
    setCopying(true);
    try {
      const ids = [...selectedIds];
      const result = await copyRicefwItems(projectId, ids);
      if (result.success) {
        showSnackbar(result.message);
        setSelectedIds(new Set());
        await fetchItems();
      } else {
        showSnackbar(result.error || 'Copy failed', 'error');
      }
    } catch (e) {
      showSnackbar('Copy failed: ' + e.message, 'error');
    } finally {
      setCopying(false);
    }
  };

  const handleCreated = (newItem) => {
    setCreateModalOpen(false);
    showSnackbar(`${newItem.ricefw_id} created successfully`);
    fetchItems();
  };

  const handleGenerateFs = async (ids) => {
    setGeneratingFsIds((s) => new Set([...s, ...ids]));
    try {
      const result = await generateFsDocuments(projectId, ids);
      if (result.ok) {
        showSnackbar(ids.length === 1 ? 'Functional Spec downloaded.' : `${ids.length} Functional Specs downloaded as ZIP.`);
      } else {
        showSnackbar(result.error || 'Failed to generate FS', 'error');
      }
    } catch (e) {
      showSnackbar('Failed to generate FS: ' + e.message, 'error');
    } finally {
      setGeneratingFsIds((s) => { const n = new Set(s); ids.forEach((id) => n.delete(id)); return n; });
    }
  };

  const handleGenerateTs = async (ids) => {
    setGeneratingTsIds((s) => new Set([...s, ...ids]));
    try {
      const result = await generateTsDocuments(projectId, ids);
      if (result.ok) {
        showSnackbar(ids.length === 1 ? 'Technical Spec downloaded.' : `${ids.length} Technical Specs downloaded as ZIP.`);
      } else {
        showSnackbar(result.error || 'Failed to generate TS', 'error');
      }
    } catch (e) {
      showSnackbar('Failed to generate TS: ' + e.message, 'error');
    } finally {
      setGeneratingTsIds((s) => { const n = new Set(s); ids.forEach((id) => n.delete(id)); return n; });
    }
  };

  // Sort
  const handleSortClick = (field) => {
    if (sortField === field) setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortOrder('asc'); }
  };

  const hasActiveFilters = search || Object.values(filters).some(Boolean);
  const uniqueTypes = useMemo(() => [...new Set(items.map((i) => i.ricefw_type).filter(Boolean))].sort(), [items]);

  const processedItems = useMemo(() => {
    let result = items;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter((item) => VISIBLE_COLUMNS.some((col) => (item[col.field] ?? '').toString().toLowerCase().includes(q)));
    }
    if (filters.type) result = result.filter((i) => i.ricefw_type === filters.type);
    return [...result].sort(getComparator(sortOrder, sortField));
  }, [items, search, filters, sortField, sortOrder]);

  const pageItems = useMemo(() => processedItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage), [processedItems, page, rowsPerPage]);

  // Select all on current page
  const allPageSelected = pageItems.length > 0 && pageItems.every((i) => selectedIds.has(i.id));
  const somePageSelected = pageItems.some((i) => selectedIds.has(i.id));
  const toggleSelectAll = () => {
    if (allPageSelected) setSelectedIds((s) => { const n = new Set(s); pageItems.forEach((i) => n.delete(i.id)); return n; });
    else setSelectedIds((s) => { const n = new Set(s); pageItems.forEach((i) => n.add(i.id)); return n; });
  };
  const toggleSelect = (id) => setSelectedIds((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const TypeChip = ({ type }) => {
    const meta = TYPE_COLORS[type] || { bg: '#F5F5F5', color: '#424242', label: type };
    return <Chip label={`${type} – ${meta.label}`} size="small" sx={{ bgcolor: meta.bg, color: meta.color, fontWeight: 600, fontSize: '0.7rem' }} />;
  };

  const getSpecStatus = (itemId, specType) => specSummary[String(itemId)]?.[specType] || null;

  const getSpecTooltip = (item, specType) => {
    if (specType === 'functional' && item.ricefw_type === 'W') return 'FS template not yet available for Workflow';
    const doc = getSpecStatus(item.id, specType);
    if (!doc) return specType === 'functional' ? 'Open Functional Spec options' : 'Open Technical Spec options';
    const actor = doc.created_by_name || 'Unknown';
    const date = formatDateTime(doc.created_at);
    return `${doc.version_label} latest - ${doc.source === 'uploaded' ? 'uploaded' : 'created'} by ${actor} on ${date}`;
  };

  const typeCounts = useMemo(() => items.reduce((acc, i) => { acc[i.ricefw_type] = (acc[i.ricefw_type] || 0) + 1; return acc; }, {}), [items]);

  const busy = generating || downloading || previewing || deleting || copying;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} gutterBottom>RICEFW List</Typography>
          <Typography variant="body2" color="text.secondary">
            Auto-generated from Gap Analysis backlog. Generate or regenerate, download, modify in Excel, then upload back.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <Button data-tour="generate-btn" variant="contained" startIcon={(loading || generating) ? <CircularProgress size={16} color="inherit" /> : <AutoFixHighIcon />}
            onClick={() => setConfirmGenerateOpen(true)} disabled={loading || busy}
            sx={{ bgcolor: '#9C27B0', '&:hover': { bgcolor: '#7B1FA2' } }}>
            {loading ? 'Loading…' : generating ? 'Generating…' : items.length > 0 ? 'Regenerate RICEFW List' : 'Generate RICEFW List'}
          </Button>
          <Button data-tour="download-btn" variant="outlined" startIcon={downloading ? <CircularProgress size={16} /> : <DownloadIcon />}
            onClick={handleDownload} disabled={busy || items.length === 0}
            sx={{ borderColor: '#9C27B0', color: '#9C27B0', '&:hover': { borderColor: '#7B1FA2', bgcolor: 'rgba(156,39,176,0.04)' } }}>
            {downloading ? 'Downloading…' : 'Download Excel'}
          </Button>
          <Button data-tour="upload-btn" variant="outlined" startIcon={previewing ? <CircularProgress size={16} /> : <UploadFileIcon />}
            onClick={() => fileInputRef.current?.click()} disabled={busy || items.length === 0}
            sx={{ borderColor: '#2E7D32', color: '#2E7D32', '&:hover': { borderColor: '#1B5E20', bgcolor: 'rgba(46,125,50,0.04)' } }}>
            {previewing ? 'Parsing…' : 'Upload Excel'}
          </Button>
          <input ref={fileInputRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleFileSelected} />
          <Button variant="outlined" startIcon={<AddIcon />}
            onClick={() => setCreateModalOpen(true)} disabled={busy}
            sx={{ borderColor: '#E65100', color: '#E65100', '&:hover': { borderColor: '#BF360C', bgcolor: 'rgba(230,81,0,0.04)' } }}>
            Add New Row
          </Button>
        </Box>
      </Box>

      {/* Summary chips */}
      {items.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>{items.length} items:</Typography>
          {Object.entries(typeCounts).sort().map(([type, count]) => {
            const meta = TYPE_COLORS[type] || { bg: '#F5F5F5', color: '#424242', label: type };
            return <Chip key={type} label={`${meta.label} (${count})`} size="small" sx={{ bgcolor: meta.bg, color: meta.color, fontWeight: 600 }} />;
          })}
        </Box>
      )}

      {items.length > 0 && (
        <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 2 }}>
          <strong>Tip:</strong> To download <strong>Functional Specifications</strong> or <strong>Technical Specifications</strong>,
          select one or more RICEFW items from the list using the checkboxes, then use the bulk action buttons that appear in the filter bar.
          You can also open per-row document options from the Func. Spec and Tech. Spec columns.
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Search + Filters + Bulk delete */}
      {items.length > 0 && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControl data-tour="type-filter" size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Type</InputLabel>
              <Select label="Type" value={filters.type} onChange={(e) => setFilters({ type: e.target.value })}>
                <MenuItem value=""><em>All Types</em></MenuItem>
                {uniqueTypes.map((t) => <MenuItem key={t} value={t}>{t} – {TYPE_COLORS[t]?.label || t}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField data-tour="search-box" size="small" placeholder="Search all fields…" value={search}
              onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 260, flex: '1 1 260px' }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                endAdornment: search ? <InputAdornment position="end"><IconButton size="small" onClick={() => setSearch('')}><ClearIcon fontSize="small" /></IconButton></InputAdornment> : null,
              }} />
            {hasActiveFilters && (
              <>
                <Button size="small" startIcon={<FilterListIcon />} onClick={() => { setFilters(EMPTY_FILTERS); setSearch(''); }} color="secondary" variant="outlined">Clear all</Button>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                  {processedItems.length} of {items.length} shown
                </Typography>
              </>
            )}
            {selectedIds.size > 0 && (
              <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
                <Button size="small" variant="contained"
                  startIcon={copying ? <CircularProgress size={14} color="inherit" /> : <ContentCopyIcon />}
                  onClick={handleCopy}
                  disabled={copying}
                  sx={{ bgcolor: '#F57F17', '&:hover': { bgcolor: '#E65100' } }}>
                  {copying ? 'Copying…' : `Copy & Create (${selectedIds.size})`}
                </Button>
                <Button size="small" variant="contained"
                  startIcon={generatingFsIds.size > 0 ? <CircularProgress size={14} color="inherit" /> : <DescriptionIcon />}
                  onClick={() => setSpecConfirm({ type: 'functional', ids: [...selectedIds] })}
                  disabled={generatingFsIds.size > 0}
                  sx={{ bgcolor: '#1565C0', '&:hover': { bgcolor: '#0D47A1' } }}>
                  Generate Functional Spec ({selectedIds.size})
                </Button>
                <Button size="small" variant="contained"
                  startIcon={generatingTsIds.size > 0 ? <CircularProgress size={14} color="inherit" /> : <ArticleIcon />}
                  onClick={() => setSpecConfirm({ type: 'technical', ids: [...selectedIds] })}
                  disabled={generatingTsIds.size > 0}
                  sx={{ bgcolor: '#00695C', '&:hover': { bgcolor: '#004D40' } }}>
                  Generate Technical Spec ({selectedIds.size})
                </Button>
                <Button size="small" variant="contained" color="error" startIcon={<DeleteIcon />}
                  onClick={() => setConfirmDeleteOpen(true)}>
                  Delete {selectedIds.size} selected
                </Button>
              </Box>
            )}
          </Box>
        </Paper>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
      ) : items.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 5, textAlign: 'center', borderStyle: 'dashed' }}>
          <AutoFixHighIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>No RICEFW items yet</Typography>
          <Typography variant="body2" color="text.secondary">
            Click <strong>Generate RICEFW List</strong> to populate from your Gap Analysis backlog.
          </Typography>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 360px)', overflow: 'auto' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {/* Checkbox + Actions header */}
                  <TableCell data-tour="table-header-checkbox" padding="checkbox" sx={{ bgcolor: '#4472C4', borderRight: '1px solid rgba(255,255,255,0.2)', position: 'sticky', left: 0, zIndex: 3 }}>
                    <Checkbox checked={allPageSelected} indeterminate={somePageSelected && !allPageSelected}
                      onChange={toggleSelectAll} sx={{ color: '#fff', '&.Mui-checked': { color: '#fff' }, '&.MuiCheckbox-indeterminate': { color: '#fff' } }} />
                  </TableCell>
                  <TableCell data-tour="actions-header" sx={{ fontWeight: 700, bgcolor: '#4472C4', color: '#fff', whiteSpace: 'nowrap', minWidth: 80, fontSize: '0.78rem', position: 'sticky', left: 52, zIndex: 3 }}>
                    Actions
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: '#4472C4', color: '#90EE90', whiteSpace: 'nowrap', fontSize: '0.78rem', textAlign: 'center' }}>
                    Func. Spec
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: '#4472C4', color: '#80CBC4', whiteSpace: 'nowrap', fontSize: '0.78rem', textAlign: 'center' }}>
                    Tech. Spec
                  </TableCell>
                  {VISIBLE_COLUMNS.map((col) => (
                    <TableCell key={col.field} sortDirection={sortField === col.field ? sortOrder : false}
                      sx={{ fontWeight: 700, bgcolor: '#4472C4', color: '#fff', whiteSpace: 'nowrap', minWidth: col.width, fontSize: '0.78rem',
                        '& .MuiTableSortLabel-root': { color: '#fff' },
                        '& .MuiTableSortLabel-root:hover': { color: '#E3F2FD' },
                        '& .MuiTableSortLabel-root.Mui-active': { color: '#fff' },
                        '& .MuiTableSortLabel-icon': { color: '#fff !important' },
                      }}>
                      <TableSortLabel active={sortField === col.field} direction={sortField === col.field ? sortOrder : 'asc'} onClick={() => handleSortClick(col.field)}>
                        {col.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {pageItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={VISIBLE_COLUMNS.length + 4} sx={{ textAlign: 'center', py: 4, color: '#666' }}>
                      No items match your search or filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  pageItems.map((item, idx) => {
                    const isSelected = selectedIds.has(item.id);
                    return (
                      <TableRow key={item.id}
                        onClick={() => { setEditItemReadOnly(true); setEditItem(item); }}
                        sx={{ cursor: 'pointer', bgcolor: isSelected ? 'rgba(156,39,176,0.18)' : idx % 2 === 0 ? '#263238' : '#37474F', '&:hover': { bgcolor: isSelected ? 'rgba(156,39,176,0.25)' : '#546E7A' } }}>
                        {/* Checkbox */}
                        <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()} sx={{ borderColor: 'rgba(255,255,255,0.08)', position: 'sticky', left: 0, bgcolor: isSelected ? 'rgba(156,39,176,0.18)' : idx % 2 === 0 ? '#263238' : '#37474F', zIndex: 1 }}>
                          <Checkbox checked={isSelected} onChange={() => toggleSelect(item.id)}
                            sx={{ color: 'rgba(255,255,255,0.6)', '&.Mui-checked': { color: '#CE93D8' } }} />
                        </TableCell>
                        {/* Action buttons */}
                        <TableCell onClick={(e) => e.stopPropagation()} sx={{ borderColor: 'rgba(255,255,255,0.08)', position: 'sticky', left: 52, bgcolor: isSelected ? 'rgba(156,39,176,0.18)' : idx % 2 === 0 ? '#263238' : '#37474F', zIndex: 1 }}>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="Edit row">
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); setEditItemReadOnly(false); setEditItem(item); }} sx={{ color: '#90CAF9', '&:hover': { bgcolor: 'rgba(144,202,249,0.15)' } }}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete row">
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeleteSingle(item); }} sx={{ color: '#EF9A9A', '&:hover': { bgcolor: 'rgba(239,154,154,0.15)' } }}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                        {/* Functional Spec */}
                        <TableCell onClick={(e) => e.stopPropagation()} sx={{ borderColor: 'rgba(255,255,255,0.08)', bgcolor: isSelected ? 'rgba(156,39,176,0.18)' : idx % 2 === 0 ? '#263238' : '#37474F', textAlign: 'center' }}>
                          <Tooltip title={getSpecTooltip(item, 'functional')}>
                            <span>
                              <IconButton size="small"
                                onClick={() => setSpecDialog({ item, specType: 'functional' })}
                                disabled={item.ricefw_type === 'W'}
                                sx={{ color: '#90EE90', '&:hover': { bgcolor: 'rgba(144,238,144,0.15)' }, '&.Mui-disabled': { color: 'rgba(255,255,255,0.2)' } }}>
                                <DescriptionIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                        {/* Technical Spec */}
                        <TableCell onClick={(e) => e.stopPropagation()} sx={{ borderColor: 'rgba(255,255,255,0.08)', bgcolor: isSelected ? 'rgba(156,39,176,0.18)' : idx % 2 === 0 ? '#263238' : '#37474F', textAlign: 'center' }}>
                          <Tooltip title={getSpecTooltip(item, 'technical')}>
                            <span>
                              <IconButton size="small"
                                onClick={() => setSpecDialog({ item, specType: 'technical' })}
                                disabled={false}
                                sx={{ color: '#80CBC4', '&:hover': { bgcolor: 'rgba(128,203,196,0.15)' }, '&.Mui-disabled': { color: 'rgba(255,255,255,0.2)' } }}>
                                <ArticleIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                        {/* Data cells */}
                        {VISIBLE_COLUMNS.map((col) => (
                          <TableCell key={col.field} sx={{ fontSize: '0.8rem', verticalAlign: 'top', py: 0.75, color: '#FFFFFF', borderColor: 'rgba(255,255,255,0.08)' }}>
                            {col.field === 'ricefw_type' ? (
                              <TypeChip type={item.ricefw_type} />
                            ) : col.field === 'ricefw_id' ? (
                              <Typography variant="body2" fontFamily="monospace" fontWeight={600} color="#FFFFFF">{item.ricefw_id}</Typography>
                            ) : (
                              <Tooltip title={item[col.field] || ''} placement="top-start" enterDelay={500}>
                                <Typography variant="body2" sx={{ color: '#FFFFFF', maxWidth: col.width, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                  {item[col.field] || ''}
                                </Typography>
                              </Tooltip>
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination component="div" count={processedItems.length} page={page}
            onPageChange={(_, newPage) => setPage(newPage)} rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[25]} sx={{ borderTop: '1px solid rgba(224,224,224,1)' }} />
        </>
      )}

      {/* Create Row Modal */}
      <CreateRowModal open={createModalOpen} projectId={projectId}
        onClose={() => setCreateModalOpen(false)} onCreated={handleCreated} />

      {/* Edit Modal */}
      <EditModal open={Boolean(editItem)} item={editItem} onClose={() => setEditItem(null)} onSaved={handleEditSaved} initialReadOnly={editItemReadOnly} />

      {/* Upload Preview Modal */}
      <PreviewModal open={previewOpen} previewData={previewData} pendingFile={pendingFile}
        projectId={projectId} onClose={handlePreviewClose} onApplied={handleApplied} />

      <SpecDocumentDialog
        open={Boolean(specDialog)}
        projectId={projectId}
        item={specDialog?.item}
        specType={specDialog?.specType}
        onClose={() => setSpecDialog(null)}
        onChanged={fetchSpecSummary}
        showSnackbar={showSnackbar}
      />

      {/* Generate Confirmation */}
      <Dialog open={confirmGenerateOpen} onClose={() => !generating && setConfirmGenerateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon sx={{ color: '#E65100' }} />
          {items.length > 0 ? 'Regenerate RICEFW List?' : 'Generate RICEFW List?'}
        </DialogTitle>
        <DialogContent>
          {items.length > 0 ? (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>All existing data will be overwritten.</strong>
              </Alert>
              <Typography variant="body2">
                Regenerating will <strong>delete all {items.length} current RICEFW items</strong> and rebuild the list
                fresh from the backlog. Any manual edits or uploaded changes — including priorities, owners,
                dates, comments, and status fields — will be permanently lost.
              </Typography>
              <Typography variant="body2" sx={{ mt: 1.5, color: 'text.secondary' }}>
                If you want to keep your changes, download the Excel first before proceeding.
              </Typography>
            </>
          ) : (
            <Typography variant="body2">
              This action will create the RICEFW list using the current Gap Analysis backlog.
            </Typography>
          )}
          {generating && <LinearProgress sx={{ mt: 2 }} />}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmGenerateOpen(false)} disabled={generating}>Cancel</Button>
          <Button variant="contained" color="warning" disabled={generating}
            onClick={async () => { await handleGenerate(); setConfirmGenerateOpen(false); }}
            startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <AutoFixHighIcon />}>
            {generating ? 'Generating…' : items.length > 0 ? 'Yes, Regenerate' : 'Yes, Generate'}
          </Button>
        </DialogActions>
      </Dialog>

      <SafetyConfirmDialog
        open={Boolean(specConfirm)}
        title={`Generate ${specConfirm?.type === 'functional' ? 'Functional' : 'Technical'} Specifications?`}
        warning={`AI specifications will be generated for ${specConfirm?.ids?.length || 0} selected item(s).`}
        paragraphs={[
          `This action will use AI to generate ${specConfirm?.type === 'functional' ? 'Functional' : 'Technical'} Specifications for ${specConfirm?.ids?.length || 0} selected item(s) and may take several minutes. Avoid repeating generation unless the source data has changed.`
        ]}
        confirmLabel={`Yes, Generate ${specConfirm?.ids?.length || 0}`}
        loading={generatingFsIds.size > 0 || generatingTsIds.size > 0}
        onCancel={() => setSpecConfirm(null)}
        onConfirm={() => {
          const pending = specConfirm;
          setSpecConfirm(null);
          if (pending?.type === 'functional') handleGenerateFs(pending.ids);
          if (pending?.type === 'technical') handleGenerateTs(pending.ids);
        }}
      />

      {/* Delete Confirmation */}
      <Dialog open={confirmDeleteOpen} onClose={() => !deleting && setConfirmDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{selectedIds.size} item{selectedIds.size !== 1 ? 's' : ''}</strong>? This cannot be undone.
          </Typography>
          {deleting && <LinearProgress sx={{ mt: 2 }} />}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDeleteOpen(false)} disabled={deleting}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Joyride interactive tour */}
      <Joyride
        steps={tourSteps}
        run={tourRun}
        continuous
        showSkipButton
        showProgress
        scrollToFirstStep
        spotlightClicks={false}
        disableOverlayClose
        callback={({ status, action }) => {
          if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status) || action === ACTIONS.CLOSE) setTourRun(false);
        }}
        styles={{
          options: {
            primaryColor: '#F5821F',
            beaconInnerColor: '#F5821F',
            beaconOuterColor: 'rgba(245,130,31,0.4)',
            backgroundColor: '#1e1e1e',
            textColor: '#ffffff',
            overlayColor: 'rgba(0,0,0,0.65)',
            arrowColor: '#1e1e1e',
            zIndex: 10000,
          },
          tooltip: { borderRadius: 8, padding: '16px 20px' },
          tooltipTitle: { fontSize: '1rem', fontWeight: 700, marginBottom: 8 },
          tooltipContent: { fontSize: '0.875rem', lineHeight: 1.6 },
          buttonNext: { backgroundColor: '#F5821F', borderRadius: 6, padding: '8px 16px' },
          buttonBack: { color: '#F5821F', marginRight: 8 },
          buttonSkip: { color: '#aaa' },
          spotlight: { borderRadius: 6 },
        }}
        locale={{ back: 'Back', close: 'Close', last: 'Done', next: 'Next', skip: 'Skip tour' }}
      />

      {/* Floating Help Button */}
      <Tooltip title="Feature Guide" placement="left">
        <Fab
          size="medium"
          onClick={() => { setTourRun(false); setTimeout(() => setTourRun(true), 50); }}
          sx={{
            position: 'fixed', bottom: 32, right: 32, zIndex: 1200,
            bgcolor: '#F5821F', color: '#fff',
            '&:hover': { bgcolor: '#E65100' },
            boxShadow: '0 4px 14px rgba(245,130,31,0.5)',
          }}
        >
          <HelpOutlineIcon />
        </Fab>
      </Tooltip>

    </Box>
  );
};

export default RICEFWListPage;
